import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faXmark,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';

export interface LightboxImage {
  id: string;
  url: string;
  filename: string;
}

interface Props {
  images: LightboxImage[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
}

export function LightboxViewer({ images, initialIndex, open, onClose }: Props) {
  const [current, setCurrent] = useState(initialIndex);

  useEffect(() => {
    setCurrent(initialIndex);
  }, [initialIndex, open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrent((c) => Math.max(0, c - 1));
      if (e.key === 'ArrowRight') setCurrent((c) => Math.min(images.length - 1, c + 1));
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, images.length]);

  if (!open || images.length === 0) return null;

  const img = images[current];
  if (!img) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <div className="absolute top-4 left-1/2 -translate-x-1/2 select-none text-sm text-white/60">
        {current + 1} / {images.length}
      </div>

      <button
        className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center text-white/80 transition hover:text-white"
        onClick={onClose}
        aria-label="Close"
      >
        <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
      </button>

      <a
        href={img.url}
        download={img.filename}
        className="absolute top-4 right-16 flex h-9 w-9 items-center justify-center text-white/80 transition hover:text-white"
        onClick={(e) => e.stopPropagation()}
        aria-label="Download image"
      >
        <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
      </a>

      {current > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center text-white/80 transition hover:text-white"
          onClick={(e) => {
            e.stopPropagation();
            setCurrent((c) => c - 1);
          }}
          aria-label="Previous image"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-6 w-6" />
        </button>
      )}

      {current < images.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center text-white/80 transition hover:text-white"
          onClick={(e) => {
            e.stopPropagation();
            setCurrent((c) => c + 1);
          }}
          aria-label="Next image"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-6 w-6" />
        </button>
      )}

      <div className="max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        <img
          src={img.url}
          alt={img.filename}
          className="max-h-[90vh] max-w-[90vw] object-contain"
        />
      </div>
    </div>,
    document.body,
  );
}
